import { Facebook, Linkedin, Mail, MessageCircle, ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/layout/Seo";
import { MaskedLines, Reveal } from "@/components/motion/Reveal";
import { EnquiryForm } from "@/components/sections/EnquiryForm";
import { channels } from "@/config/site";

const ICONS = { whatsapp: MessageCircle, email: Mail, linkedin: Linkedin, facebook: Facebook };
const CTA_LABEL = { whatsapp: "Chat on WhatsApp", email: "Send an Email", linkedin: "Follow on LinkedIn", facebook: "Follow on Facebook" };

const Channel = ({ c }) => {
  const Icon = ICONS[c.key];
  const body = (
    <>
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold-light"><Icon className="h-5 w-5" strokeWidth={1.5} /></span>
      <span className="flex-1">
        <span className="block text-[15px] font-semibold text-white">{CTA_LABEL[c.key]}</span>
        <span className="block text-[13px] text-white/60">{c.href ? c.value : "Details coming soon"}</span>
      </span>
      {c.href && <ArrowUpRight className="h-5 w-5 text-gold-light transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />}
    </>
  );
  const cls = "group flex min-h-[72px] items-center gap-4 border-t border-white/10 py-4";
  return c.href ? (
    <a href={c.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`contact-channel-${c.key}`}>{body}</a>
  ) : (
    <div className={`${cls} opacity-70`} aria-disabled="true" data-testid={`contact-channel-${c.key}`}>{body}</div>
  );
};

export default function Contact() {
  return (
    <>
      <Seo title="Contact VION Tech | Let's Start a Conversation" description="Contact VION Tech about technology training, AI solutions, corporate partnerships, international education support or strategic collaboration." path="/contact" />
      <section className="grain relative overflow-hidden bg-ink pb-20 pt-32 sm:pt-40" data-testid="contact-hero">
        <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-0 h-[520px] w-[520px] rounded-full bg-plum-3/40 blur-[120px]" />
        <div className="shell relative z-[2] grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow flex items-center gap-3 text-gold"><span className="h-px w-10 bg-gold" /> Contact</p>
            <MaskedLines testid="contact-heading" lines={["Let's Start a", "Conversation."]} lineClassName={(i) => (i === 1 ? "italic text-gold-metal shimmer" : "")} className="mt-7 font-display text-[2.6rem] font-light leading-[1.02] tracking-[-0.02em] text-white sm:text-6xl lg:text-[4.4rem]" />
            <Reveal as="p" delay={0.3} className="mt-8 max-w-md text-base leading-relaxed text-white/75 md:text-lg">
              Whether you are looking for technology training, AI solutions, corporate partnerships, international education support or strategic collaboration, we'd love to hear from you.
            </Reveal>
            <Reveal delay={0.4} className="mt-12 border-b border-white/10">
              {channels().map((c) => <Channel key={c.key} c={c} />)}
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-7">
            <div className="rounded-[28px] bg-ivory p-6 sm:p-10 lg:p-12">
              <p className="eyebrow text-plum-2">Send an enquiry</p>
              <h2 className="mt-3 font-display text-3xl font-light text-ink sm:text-4xl">How can we help?</h2>
              <div className="mt-8"><EnquiryForm /></div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
